import {
  faFileExcel,
  faPlus,
  faScaleUnbalanced,
  faX,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { colors } from "../../../../global";

const WeightBreakColumns = ({
  metadata,
  handleWeightBreakColumnChange,
  removeWeightBreakColumn,
  addWeightBreakColumn,
  openExcelModal,
  excelSelectableFields,
}) => {
  const fields = [
    "break_type",
    "item_type",
    "break_from",
    "break_to",
    "price_buy",
    "price_sell",
  ];

  const formatLabel = (key) => {
    return key.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
  };

  const getFieldDescription = (key) => {
    const descriptions = {
      name: "Unique identifier for the weight break column",
      target_column: "Excel column to apply weight break to",
      Item_Type: "Type of item for categorization",
      from: "Starting Weight Break",
      to: "Ending Weight Break",
      rename_column: "New name for the column",
    };
    return descriptions[key] || "";
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold" style={{ color: colors.primary }}>
          Weight Break
        </h3>
        <div className="flex items-center gap-2">
          <FontAwesomeIcon
            icon={faScaleUnbalanced}
            size={16}
            style={{ color: colors.darkGray }}
          />
          <span className="text-sm" style={{ color: colors.darkGray }}>
            {metadata.weight_break_columns.length} Weight Break
            {metadata.weight_break_columns.length !== 1 ? "s" : ""}
          </span>
        </div>
      </div>

      {/* weighBreak Column List */}
      <div className="space-y-4">
        {metadata.weight_break_columns.map((weighBreak, index) => (
          <div
            key={index}
            className="group relative bg-white rounded-xl border border-gray-200 p-6 hover:shadow-sm transition-all duration-200"
          >
            {/* Column Header */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div
                  className="w-8 h-8 rounded-lg flex items-center justify-center text-white text-sm font-semibold"
                  style={{ backgroundColor: colors.primary }}
                >
                  {index + 1}
                </div>
                <div>
                  <span
                    className="text-sm font-medium"
                    style={{ color: colors.primary }}
                  >
                    Weight Break Column {index + 1}
                  </span>
                  {weighBreak.name && (
                    <p className="text-xs" style={{ color: colors.darkGray }}>
                      {weighBreak.name}
                    </p>
                  )}
                </div>
              </div>
              <button
                onClick={() => removeWeightBreakColumn(index)}
                className="opacity-0 group-hover:opacity-100 w-8 h-8 rounded-full flex items-center justify-center transition-all duration-200 hover:bg-red-50"
                style={{ color: colors.error }}
                type="button"
                aria-label="Remove Weigh Break Column"
              >
                <FontAwesomeIcon icon={faX} size="16" />
              </button>
            </div>

            {/* Input Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {fields.map((key) => {
                const isExcelSelectable = excelSelectableFields.includes(key);
                return (
                  <div key={key} className="space-y-2">
                    <div className="flex items-center justify-between">
                      <label
                        className="text-sm font-medium"
                        style={{ color: colors.darkGray }}
                      >
                        {formatLabel(key)}
                      </label>
                      {getFieldDescription(key) && (
                        <div
                          className="group/tooltip relative cursor-help"
                          title={getFieldDescription(key)}
                        >
                          <div
                            className="w-4 h-4 rounded-full flex items-center justify-center text-xs"
                            style={{
                              backgroundColor: colors.gray,
                              color: colors.darkGray,
                            }}
                          >
                            ?
                          </div>
                        </div>
                      )}
                    </div>

                    {isExcelSelectable ? (
                      <div className="flex">
                        <input
                          type="text"
                          value={weighBreak[key] || ""}
                          className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-l-lg focus:outline-none focus:ring-2 focus:border-transparent transition-all duration-200"
                          style={{
                            "--tw-ring-color": colors.primary + "40",
                            borderColor: weighBreak[key]
                              ? colors.primary + "40"
                              : undefined,
                          }}
                          placeholder={`Enter ${formatLabel(
                            key
                          ).toLowerCase()}`}
                          onChange={(e) =>
                            handleWeightBreakColumnChange(
                              index,
                              key,
                              e.target.value
                            )
                          }
                        />
                        {openExcelModal &&
                          (metadata.file_name.endsWith(".csv") ||
                            metadata?.sheet_information?.sheet_name) && (
                            <button
                              onClick={() => openExcelModal(index, key)}
                              type="button"
                              aria-label="Open Excel Sheet"
                              className="px-3 py-2 border border-l-0 border-gray-200 rounded-r-lg hover:bg-gray-50 transition-colors duration-200 flex items-center justify-center"
                              style={{ color: colors.primary }}
                            >
                              <FontAwesomeIcon icon={faFileExcel} size="16" />
                            </button>
                          )}
                      </div>
                    ) : (
                      <input
                        type="text"
                        value={weighBreak[key] || ""}
                        className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:border-transparent transition-all duration-200"
                        style={{
                          "--tw-ring-color": colors.primary + "40",
                          borderColor: weighBreak[key]
                            ? colors.primary + "40"
                            : undefined,
                        }}
                        placeholder={`Enter ${formatLabel(key).toLowerCase()}`}
                        onChange={(e) =>
                          handleWeightBreakColumnChange(
                            index,
                            key,
                            e.target.value
                          )
                        }
                      />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <button
        onClick={addWeightBreakColumn}
        className="w-full py-4 px-6 border-2 border-dashed border-gray-200 rounded-xl flex items-center justify-center gap-3 text-sm font-medium hover:border-gray-300 hover:bg-gray-50 transition-all duration-200 group"
        style={{ color: colors.primary }}
        type="button"
      >
        <FontAwesomeIcon
          icon={faPlus}
          size={18}
          className="group-hover:scale-110 transition-transform duration-200"
        />
        Add Weight Break Column
      </button>
    </div>
  );
};

export default WeightBreakColumns;
